import { api } from "../../api"

import { makeUrlSearchParams } from "../../utils/url"

import { makeStorageFileApiEndpoints } from "./endpoints"

import { StorageFileDownloadUrlResponse, StorageFileResponse } from "./storage-file-response"

import { makeStorageFileViewModel, StorageFileViewModel } from "./storage-file"

const apiEndpoints = makeStorageFileApiEndpoints()

export const storageFileApi = api.injectEndpoints({
  endpoints: (builder) => {
    return {
      getStorageFiles: builder.query<StorageFileViewModel[], GetStorageFilesQuery>({
        query: (params) => ({
          method: "GET",
          url: apiEndpoints.files(params ? makeUrlSearchParams(params) : undefined),
        }),

        transformResponse: (response: StorageFileResponse[]) => response.map(makeStorageFileViewModel),

        providesTags: ["StorageFile"],
      }),

      generatePresignedUploadUrls: builder.mutation<
        GeneratePresignedUploadUrlsResponse,
        GeneratePresignedUploadUrlsRequest
      >({
        query: (body) => ({
          method: "POST",
          url: apiEndpoints.presignedUrls(),
          body,
        }),
      }),

      confirmUpload: builder.mutation<
        void,
        {
          key: string
        }
      >({
        query: (body) => ({
          method: "POST",
          url: apiEndpoints.confirmUpload(),
          body,
        }),
      }),

      attachFiles: builder.mutation<void, AttachFilesRequest>({
        query: (body) => ({
          method: "POST",
          url: apiEndpoints.attachFiles(),
          body,
        }),

        invalidatesTags: ["StorageFile", "Project", "Task"],
      }),

      getStorageFileDownloadUrl: builder.query<StorageFileDownloadUrlResponse, string>({
        query: (fileId) => ({
          method: "GET",
          url: apiEndpoints.fileUrl(fileId),
        }),
      }),

      deleteStorageFile: builder.mutation<
        void,
        {
          fileId: string
        }
      >({
        query: ({ fileId }) => ({
          method: "DELETE",
          url: apiEndpoints.fileItem(fileId),
        }),

        invalidatesTags: ["StorageFile", "Project", "Task"],
      }),
    }
  },
})

export const {
  useGetStorageFilesQuery,
  useGeneratePresignedUploadUrlsMutation,
  useConfirmUploadMutation,
  useAttachFilesMutation,
  useGetStorageFileDownloadUrlQuery,
  useLazyGetStorageFileDownloadUrlQuery,
  useDeleteStorageFileMutation,
} = storageFileApi

export type GetStorageFilesQuery = {
  entityId: string
  entityType: "project" | "task" | "message"
}

export type GeneratePresignedUploadUrlsRequest = {
  files: {
    filename: string
    contentType: string
  }[]
  orgId?: string
}

export type GeneratePresignedUploadUrlsResponse = {
  files: {
    id: string
    key: string
    uploadUrl: string
  }[]
}

export type AttachFilesRequest = {
  keys: string[]
  entityId: string
  entityType: "project" | "task" | "message"
}
